import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, Users, Presentation, MapPin, TrendingUp, ArrowRight, LucideIcon } from "lucide-react";

// Images imports
import createursImg from "@/assets/usecases/createurs.webp";
import coachsImg from "@/assets/usecases/coachs.webp";
import conferencesImg from "@/assets/usecases/conferences.webp";
import localImg from "@/assets/usecases/local.webp";
import agencesImg from "@/assets/usecases/agences.webp";

interface UseCase {
  title: string;
  problem: string;
  solution: string;
  image: string;
  imagePosition: "left" | "right";
  icon: LucideIcon;
  link: string;
}

const useCases: UseCase[] = [
  {
    title: "Pour les créateurs & influenceurs",
    problem: "Votre portée dépend d'un algorithme, pas de vous. Votre dernière vidéo ou lancement se perd dans un flux saturé. Vos posts touchent 5% de votre audience, vos emails finissent en spam.",
    solution: "Annoncez vos nouveaux contenus à l'instant où ils sortent. Contournez les algorithmes et placez votre message directement sur l'écran verrouillé de vos abonnés. Visibilité garantie.",
    image: createursImg,
    imagePosition: "right",
    icon: Megaphone,
    link: "/cas-utilisation/createurs-influenceurs",
  },
  {
    title: "Pour les coachs & communautés",
    problem: "Vos annonces importantes (lives, nouveaux contenus, changements d'horaires) se noient dans les emails ou les fils de discussion. Résultat : taux de présence en baisse, engagement qui chute.",
    solution: "Votre carte Wallet devient la carte VIP de vos membres. Prévenez instantanément toute votre communauté d'un événement ou d'une mise à jour urgente. Présence et engagement multipliés.",
    image: coachsImg,
    imagePosition: "left",
    icon: Users,
    link: "/cas-utilisation/coachs-communautes",
  },
  {
    title: "Pour les conférenciers & organisateurs d'événements",
    problem: "Votre audience quitte la salle et vous n'avez aucun moyen fiable de rester en contact. Les cartes de visite se perdent, les promesses de \"je te follow sur LinkedIn\" s'oublient.",
    solution: "Affichez un QR code sur votre présentation. Proposez une ressource exclusive via une carte Wallet et transformez instantanément une audience temporaire en canal de communication permanent.",
    image: conferencesImg,
    imagePosition: "right",
    icon: Presentation,
    link: "/cas-utilisation/conferenciers-evenements",
  },
  {
    title: "Pour les commerces locaux",
    problem: "Vous avez besoin de faire venir du monde en boutique, mais impossible de toucher vos clients au bon moment. Quand vous avez une promo ou du nouveau stock, ils ne le savent que trop tard.",
    solution: "Envoyez une offre spéciale directement sur l'écran verrouillé de vos clients fidèles. Avec la géolocalisation, alertez-les au moment précis où ils passent près de votre commerce ou de votre franchise. Transformez un mardi calme en journée record.",
    image: localImg,
    imagePosition: "left",
    icon: MapPin,
    link: "/cas-utilisation/commerces-locaux",
  },
  {
    title: "Pour les agences marketing",
    problem: "Vos clients exigent des résultats, mais les canaux classiques saturent et perdent en efficacité. Vous cherchez un nouveau service à forte marge qui rende votre agence indispensable.",
    solution: "Proposez le marketing Wallet comme service à vos clients. Lancez un nouveau canal de communication pour une entreprise en moins d'une heure, livrez des métriques d'engagement impressionnantes et créez une source de revenus qui fidélise vos clients.",
    image: agencesImg,
    imagePosition: "right",
    icon: TrendingUp,
    link: "/cas-utilisation/agences",
  },
];

interface UseCaseItemProps {
  useCase: UseCase;
  index: number;
}

const UseCaseItem = ({ useCase, index }: UseCaseItemProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);
  const Icon = useCase.icon;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted || !itemRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(itemRef.current);
    return () => observer.disconnect();
  }, [isMounted]);

  const isImageLeft = useCase.imagePosition === "left";

  return (
    <div
      ref={itemRef}
      className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center transition-all duration-700 ease-out ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {/* Image - order changes based on position */}
      <div className={`${isImageLeft ? "lg:order-first" : "lg:order-last"} order-first`}>
        <img
          src={useCase.image}
          alt={useCase.title}
          loading="lazy"
          className="w-full h-auto rounded-2xl object-cover"
          style={{
            boxShadow: "0 8px 32px -8px rgba(0, 0, 0, 0.12), 0 4px 16px -4px rgba(0, 0, 0, 0.08)",
          }}
        />
      </div>

      {/* Text content */}
      <div className={`${isImageLeft ? "lg:order-last" : "lg:order-first"}`}>
        {/* Title with decorative line */}
        <h3 className="text-lg sm:text-xl font-bold text-[#022226] uppercase tracking-wide border-b-2 border-[#0C94A6] pb-2 inline-block mb-6">
          {useCase.title}
        </h3>

        {/* Problem section */}
        <p className="text-base font-bold text-[#074155] mb-2">Le problème :</p>
        <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] pl-4 border-l-2 border-[#0C94A6]/30 mb-6">
          {useCase.problem}
        </p>

        {/* Solution block (icon + text, NO label) */}
        <div className="flex items-start gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-[#0C94A6]/15 flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-[#0C94A6]" />
          </div>
          <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#022226] pt-2">
            {useCase.solution}
          </p>
        </div>

        {/* CTA - EXACT same style as "Commencer gratuitement" button */}
        <Link
          to={useCase.link}
          className="group inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-[hsl(187,87%,32%)] text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300 text-sm font-semibold rounded-lg"
        >
          En savoir plus
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  );
};

export const UseCases = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted || !contentRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [isMounted]);

  return (
    <section
      id="a-qui-sadresse"
      className="pt-[50px] sm:pt-[62.5px] lg:pt-[75px] pb-[50px] sm:pb-[62.5px] lg:pb-[75px] px-4 sm:px-6 lg:px-8 scroll-mt-20"
    >
      <div
        ref={contentRef}
        className={`max-w-5xl mx-auto transition-all duration-700 ease-out ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
        }`}
      >
        {/* Header - EXACT COPY from InteractiveDemo */}
        <div className="text-center mb-16">
          {/* Badge top - EXACT same style */}
          <div className="flex justify-center mb-6">
            <span className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#074155] bg-white text-sm font-semibold text-[#074155]">
              À qui s'adresse
            </span>
          </div>

          {/* Titre H2 - EXACT same style */}
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-4">
            L'alternative aux{" "}
            <span className="text-[#0C94A6]">emails et SMS marketing</span>
          </h2>

          {/* Paragraphe - EXACT same style */}
          <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] max-w-3xl mx-auto">
            <span className="text-[#0C94A6] font-semibold">UNOTIF</span> vous permet de toucher votre audience sans dépendre des algorithmes, des boîtes mail ou des SMS.
          </p>
        </div>

        {/* Use Cases Grid */}
        <div className="flex flex-col gap-16 sm:gap-20 lg:gap-24">
          {useCases.map((useCase, index) => (
            <UseCaseItem key={index} useCase={useCase} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};
